import type { Monaco } from "@monaco-editor/react";
import type { editor } from "monaco-editor";
import {
  CUSTOM_LANGUAGE_ID,
  SKILL_HIGHLIGHT_CLASS,
  SKILL_TRIGGER_CHAR,
} from "../ChatInput.constants";
import type {
  MonacoEditor,
  MonacoModel,
  MonacoPosition,
  Skill,
} from "../ChatInput.types";

export function escapeForRegex(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export interface SkillDecorationsSetup {
  updateDecorations: () => void;
  decorationType: ReturnType<MonacoEditor["createDecorationsCollection"]>;
}

export function setupSkillDecorations(
  editor: MonacoEditor,
  monaco: Monaco,
  skillsRef: React.RefObject<Skill[]>
): SkillDecorationsSetup {
  const decorationType = editor.createDecorationsCollection();

  const updateDecorations = () => {
    const model = editor.getModel();
    if (!model) return;

    const text = model.getValue();
    const decorations: editor.IModelDeltaDecoration[] = [];
    const skills = skillsRef.current || [];

    if (!skills.length) {
      decorationType.set([]);
      return;
    }

    const pattern = new RegExp(
      `(${skills.map(({ command }) => escapeForRegex(command)).join("|")})(?=\\s|$)`,
      "g"
    );

    for (const match of text.matchAll(pattern)) {
      const startPos = model.getPositionAt(match.index!);
      const endPos = model.getPositionAt(match.index! + match[0].length);
      decorations.push({
        range: new monaco.Range(
          startPos.lineNumber,
          startPos.column,
          endPos.lineNumber,
          endPos.column
        ),
        options: {
          inlineClassName: SKILL_HIGHLIGHT_CLASS,
        },
      });
    }

    decorationType.set(decorations);
  };

  return { updateDecorations, decorationType };
}

export interface CompletionProvider {
  dispose: () => void;
}

export function createCompletionProvider(
  monaco: Monaco,
  skillsRef: React.RefObject<Skill[]>
): CompletionProvider {
  const completionDisposable = monaco.languages.registerCompletionItemProvider(
    CUSTOM_LANGUAGE_ID,
    {
      triggerCharacters: [SKILL_TRIGGER_CHAR],
      provideCompletionItems: (model: MonacoModel, position: MonacoPosition) => {
        const textUntilPosition = model.getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        });

        // Only suggest if typing starts with /
        if (!textUntilPosition.endsWith(SKILL_TRIGGER_CHAR)) {
          return { suggestions: [] };
        }

        const range = {
          startLineNumber: position.lineNumber,
          startColumn: position.column - 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        };

        const skills = skillsRef.current || [];
        if (!skills.length) return { suggestions: [] };

        return {
          suggestions: skills.map(({ command, description }) => ({
            label: command,
            kind: monaco.languages.CompletionItemKind.Text,
            insertText: `${command} `,
            range,
            detail: description,
          })),
        };
      },
    }
  );

  return { dispose: () => completionDisposable.dispose() };
}
